import { useState, useEffect, useRef, useMemo } from "react";
import { useAppTheme, useIsMobile } from "./ThemeContext";
import { useAuth } from "./AuthContext";

// Objektiplaan prototype. The planner itself is the self-contained page in public/objektiplaan (no external
// requests; the DXF and its real coordinates never leave the device), hosted here inside the shell.
const SITE_KEY = "objektiplaan_site";

export default function PlanPage() {
  const { t, dark } = useAppTheme();
  const { user } = useAuth();
  const mobile = useIsMobile();
  const frame = useRef(null);
  const [sites, setSites] = useState(user?.role === "admin" ? [] : (user?.sites || []).map(s => s.name));
  const [site, setSite] = useState(() => {
    try { return localStorage.getItem(SITE_KEY) || ""; } catch { return ""; }
  });

  useEffect(() => {
    if (user?.role !== "admin") return;
    fetch("/api/sites", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : [])
      .then(list => setSites(list.map(s => s.name)))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (sites.length && !sites.includes(site)) setSite(sites[0]);
  }, [sites, site]);

  useEffect(() => {
    try { if (site) localStorage.setItem(SITE_KEY, site); } catch {}
  }, [site]);

  // Theme changes are passed in without reloading the planner, so unsaved drawing work isn't lost.
  useEffect(() => {
    const w = frame.current && frame.current.contentWindow;
    if (w) w.postMessage({ type: "theme", theme: dark ? "dark" : "light" }, window.location.origin);
  }, [dark]);

  // The theme only seeds the first load; later changes go through postMessage so the frame doesn't reload.
  const src = useMemo(() => site
    ? `/objektiplaan/index.html?site=${encodeURIComponent(site)}&theme=${dark ? "dark" : "light"}`
    : null, [site]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: "100vh", background: t.bg }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: mobile ? "12px 14px 0" : "16px 20px 0",
      }}>
        <div style={{ fontSize: mobile ? 17 : 19, fontWeight: 700, color: t.text }}>Objektiplaan</div>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 10,
          background: "#f5b94a", color: "#1b1303", letterSpacing: "0.04em",
        }}>PROTOTÜÜP</span>
        {sites.length > 1 && (
          <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: t.muted }}>
            Objekt
            <select value={site} onChange={e => setSite(e.target.value)} style={{
              fontSize: 14, padding: "8px 10px", borderRadius: 8, border: `1px solid ${t.border}`,
              background: t.panel, color: t.text, minHeight: 40,
            }}>
              {sites.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        )}
      </div>
      {src ? (
        // Keyed by site so switching sites starts the planner fresh; theme changes go through postMessage instead.
        <iframe key={site} ref={frame} src={src} title="Objektiplaan"
          style={{ flex: 1, width: "100%", minHeight: "calc(100vh - 64px - var(--shell-bottom, 0px))", border: 0, display: "block" }} />
      ) : (
        <div style={{ padding: 40, textAlign: "center", color: t.muted, fontSize: 14 }}>
          {sites.length ? "Laadin..." : "Sul pole ühtegi objekti määratud."}
        </div>
      )}
    </div>
  );
}
